/*
 * pogi-home-fx — homepage scroll-reveal.
 *  - Most content sections: the whole section fades + rises once, on enter.
 *  - Stats + Router: CHOREOGRAPHED — header then cards cascade in (~70ms apart).
 * Progressive enhancement: without JS or with prefers-reduced-motion nothing is
 * hidden (the hidden state is only ever applied here). Skips hero (own load
 * animation), sticky (fixed CTA), kit (modal) — transforming those breaks them.
 * After an element finishes revealing, its reveal classes are removed so any
 * native transitions (e.g. card hover-lift) aren't left throttled.
 */
(function () {
  const body = document.body;
  if (!body || body.getAttribute('data-template') !== 'index') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;
  // Firefox composites these full-width section reveals poorly — stutters on scroll
  // even when reduced to opacity-only. Skip the whole system in FF: no reveal classes
  // are applied, so everything renders instantly = butter-smooth scroll. Chrome/others
  // keep the animation.
  if (/firefox/i.test(navigator.userAgent)) return;

  // Section ids are TEMPLATE-PREFIXED (shopify-section-template--<hash>__hero),
  // so match by KEY SUFFIX, never by full id (the old full-id SKIP never matched
  // and the hero animated under the nav on every load — looked like a glitch).
  const SKIP_KEYS = ['__hero', '__sticky', '__kit'];
  // sections whose CHILDREN cascade instead of the whole block moving as one
  const CASCADE_KEYS = {
    '__stats': '.pogi-stats__head, .pogi-stat:not([data-pogi-promise]):not(.pogi-stat--community)',
    '__router': '.pogi-router__head, .pogi-rcard',
  };
  const cascadeFor = (id) => {
    for (const k in CASCADE_KEYS) if (id.endsWith(k)) return CASCADE_KEYS[k];
    return null;
  };
  const STEP = 0.045; // seconds between cascaded items

  const main = document.querySelector('main') || body;
  const fold = window.innerHeight * 0.9;
  const sections = Array.from(main.querySelectorAll('.shopify-section'))
    .filter((el) => {
      if (!el.id || el.id.indexOf('shopify-section-') !== 0) return false;
      if (SKIP_KEYS.some((k) => el.id.endsWith(k))) return false;
      // Anything already visible on load renders in place — above-the-fold
      // content must NEVER animate in (reads as a layout glitch).
      return el.getBoundingClientRect().top > fold;
    });
  if (!sections.length) return;

  body.classList.add('pogi-fx-ready');

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const items = e.target.__pogiItems || [e.target];
      // Prime the GPU layer ONE FRAME before the transition starts, and only on the
      // section now entering — not always-on for every section at load. Firefox holds
      // a big compositor layer per will-change element, so the all-at-once version
      // stuttered; just-in-time keeps ~one layer alive at a time. rAF gives the
      // browser a frame to promote the layer before opacity/transform animate.
      items.forEach((el) => { el.style.willChange = 'opacity, transform'; });
      requestAnimationFrame(() => items.forEach((el) => el.classList.add('is-in')));
      io.unobserve(e.target);
      // once the cascade has played, drop the reveal styles (incl. will-change) so the
      // elements' own transitions (hover, etc.) work normally again.
      const total = items.length * STEP * 1000 + 700;
      setTimeout(() => items.forEach((el) => {
        el.classList.remove('pogi-reveal', 'is-in');
        el.style.removeProperty('--reveal-delay');
        el.style.removeProperty('will-change');
      }), total);
    });
  }, { rootMargin: '0px 0px -16% 0px', threshold: 0.08 });

  sections.forEach((section) => {
    const sel = section.querySelector('.pogi-stats') ? CASCADE_KEYS.__stats : cascadeFor(section.id);
    let items = sel ? Array.from(section.querySelectorAll(sel)) : [];
    if (!items.length) items = [section];
    items.forEach((el, i) => {
      el.classList.add('pogi-reveal');
      if (items.length > 1) el.style.setProperty('--reveal-delay', (i * STEP) + 's');
    });
    section.__pogiItems = items;
    io.observe(section);
  });
})();

// Community photos settle into place once, row by row. Nothing is hidden while
// waiting: failed/unsupported animation, lazy images and no JS keep a normal grid.
(() => {
  if (window.pogiCommunityMotion) { window.pogiCommunityMotion(); return; }
  if (document.body?.getAttribute('data-template') !== 'index' ||
      !('IntersectionObserver' in window) || !Element.prototype.animate ||
      !window.matchMedia || /firefox/i.test(navigator.userAgent)) return;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!preference.addEventListener && !preference.addListener) return;
  const seen = new WeakSet();
  const grids = new Map();
  const finish = record => {
    record.animations.forEach(animation => animation.cancel());
    record.animations.clear();
  };
  const forget = record => {
    record.observer.disconnect();
    finish(record);
    grids.delete(record.grid);
  };
  const refresh = () => {
    for (const record of grids.values()) if (!record.grid.isConnected) forget(record);
    document.querySelectorAll('.pogi-stat__photos').forEach(grid => {
      if (grids.has(grid)) return;
      const photos = [...grid.querySelectorAll('img')];
      const record = { grid, photos, animations: new Map(), observer: null };
      record.observer = new IntersectionObserver(entries => {
        if (grids.get(grid) !== record || !grid.isConnected) return;
        // Resolve current tracks here: resizing across the 4/2-column breakpoint
        // must not leave a stale desktop stagger on the phone layout.
        const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
        entries.forEach(entry => {
          const photo = entry.target;
          if (!entry.isIntersecting || seen.has(photo) || !photo.isConnected || !grid.contains(photo)) return;
          seen.add(photo);
          record.observer.unobserve(photo);
          if (preference.matches || document.hidden || !photo.complete || !photo.naturalWidth) return;
          const animation = photo.animate([
            { opacity: 0.74, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ], {
            duration: 560, delay: (photos.indexOf(photo) % columns) * 45,
            easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'backwards',
          });
          record.animations.set(photo, animation);
          animation.finished.then(() => record.animations.delete(photo), () => record.animations.delete(photo));
        });
        if (photos.every(photo => seen.has(photo))) record.observer.disconnect();
      }, { threshold: 0.15, rootMargin: '0px 0px -24px 0px' });
      grids.set(grid, record);
      photos.forEach(photo => {
        // An anchor/back navigation or editor load should never animate photos
        // already on screen (or above it).
        if (preference.matches || photo.getBoundingClientRect().top < window.innerHeight) seen.add(photo);
        if (!seen.has(photo)) record.observer.observe(photo);
      });
    });
  };
  const onPreference = () => {
    if (!preference.matches) return;
    for (const record of grids.values()) {
      record.photos.forEach(photo => seen.add(photo));
      record.observer.disconnect();
      finish(record);
    }
  };
  if (preference.addEventListener) preference.addEventListener('change', onPreference);
  else preference.addListener(onPreference);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) for (const record of grids.values()) finish(record);
  });
  document.addEventListener('shopify:section:unload', event => {
    for (const record of grids.values()) if (event.target.contains(record.grid)) forget(record);
  });
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  window.pogiCommunityMotion = refresh;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
